<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <v-card-title class="pb-2 pt-3 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <h2 class="text-h6 font-weight-bold primary--text mb-0">
                    {{
                      isDeletePage() != true
                        ? `Product Variants for "${
                            product ? product.name : "Loading..."
                          }"`
                        : "Deleted Product Variants"
                    }}
                  </h2>
                  <p class="text-caption grey--text ma-0 mt-1">
                    {{
                      isDeletePage() != true
                        ? `Manage and monitor all variants for this product`
                        : "Restore or permanently delete product variants"
                    }}
                  </p>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                  class="pa-0"
                  v-if="isDeletePage() != true && positionName != 'SELLER'"
                >
                  <div class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      :to="`/products/${$route.params.productId}/product-variants/create`"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Product Variant
                    </v-btn>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="auto" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      color="grey darken-1"
                      outlined
                      small
                      @click="$router.push('/products')"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Products
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider class="mx-4"></v-divider>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Product Variants</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Product Variants"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="SKU ID, strength..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Stock Level Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.stock_level"
                    :items="stockLevels"
                    label="Stock Level"
                    prepend-inner-icon="mdi-package-variant"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <!-- Price Range Filters Row -->
              <v-row class="mt-2">
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.min_price"
                    label="Min Price"
                    prepend-inner-icon="mdi-currency-inr"
                    outlined
                    dense
                    type="number"
                    clearable
                    hide-details
                    @input="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.max_price"
                    label="Max Price"
                    prepend-inner-icon="mdi-currency-inr"
                    outlined
                    dense
                    type="number"
                    clearable
                    hide-details
                    @input="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Product Variants Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Product Variants List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-shopping</v-icon>
                      {{ count }}
                      {{ count === 1 ? "Product Variant" : "Product Variants" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage() && product_variants.length > 0"
                      x-small
                      color="success"
                      outlined
                      @click="generateExcel"
                      class="px-2"
                      :loading="isExcelloading"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading product variants..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="product_variants"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Unique ID Column -->
                <template #[`item.id`]="{ item }">
                  <v-chip
                    color="info"
                    x-small
                    outlined
                    class="font-weight-bold"
                  >
                    <v-icon left x-small>mdi-identifier</v-icon>
                    {{ item.id }}
                  </v-chip>
                </template>

                <!-- SKU ID Column -->
                <template #[`item.sku_id`]="{ item }">
                  <div class="text-caption">
                    <v-chip
                      v-if="item.sku_id"
                      color="orange"
                      x-small
                      dark
                      class="font-weight-bold"
                    >
                      <v-icon left x-small>mdi-barcode</v-icon>
                      {{ item.sku_id }}
                    </v-chip>
                    <span v-else class="grey--text">No SKU</span>
                  </div>
                </template>

                <!-- Created By Column -->
                <template #[`item.created_by_id`]="{ item }">
                  <div class="text-caption">
                    <v-chip
                      v-if="item.created_by && (item.created_by.first_name || item.created_by.last_name)"
                      color="teal"
                      x-small
                      outlined
                      class="font-weight-medium"
                    >
                      <v-icon left x-small>mdi-account</v-icon>
                      {{ item.created_by.first_name }} {{ item.created_by.last_name }}
                    </v-chip>
                    <v-chip
                      v-else-if="item.created_by_id"
                      color="grey"
                      x-small
                      outlined
                      class="font-weight-medium"
                    >
                      <v-icon left x-small>mdi-account-question</v-icon>
                      ID: {{ item.created_by_id }}
                    </v-chip>
                    <span v-else class="grey--text">System</span>
                  </div>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{
                        item.is_active ? "mdi-check-circle" : "mdi-close-circle"
                      }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Product Variant Info Column -->
                <template #[`item.product_variant_info`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2">
                      <v-img
                        v-if="getFirstProductVariantImage(item)"
                        :src="getImageUrl(getFirstProductVariantImage(item))"
                        :alt="item.name"
                      ></v-img>
                      <v-icon v-else color="grey lighten-1" size="18"
                        >mdi-package-variant</v-icon
                      >
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ item.name || "No variant name" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Unit & Packing Column -->
                <template #[`item.unit_packing`]="{ item }">
                  <div>
                    <div class="text-caption font-weight-medium">
                      Unit: {{ item.unit ? item.unit.description : "N/A" }}
                    </div>
                    <div class="text-caption grey--text">
                      Pack:
                      {{
                        item.packing_type
                          ? item.packing_type.description
                          : "N/A"
                      }}
                    </div>
                  </div>
                </template>

                <!-- Pricing Info Column -->
                <template #[`item.pricing_info`]="{ item }">
                  <div>
                    <div class="text-caption font-weight-medium">
                      MRP: ₹{{
                        item.seller_product_variants_min_maximum_retail_price
                          ? formatPrice(item.seller_product_variants_min_maximum_retail_price)
                          : "N/A"
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      Stock: {{ item.seller_product_variants_sum_stock || 0 }}
                    </div>
                  </div>
                </template>

                <!-- Price & Stock Column -->
                <template #[`item.price_stock`]="{ item }">
                  <v-tooltip bottom max-width="280" :disabled="!item.seller_product_variants || item.seller_product_variants.length === 0">
                    <template #activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on" style="cursor:pointer">
                        <div class="text-body-2 font-weight-medium success--text">
                          ₹{{ formatPrice(item.seller_product_variants_min_maximum_retail_price || 0) }}
                          <span v-if="item.seller_product_variants && item.seller_product_variants.length > 1" class="text-caption grey--text ml-1">({{ item.seller_product_variants.length }} sellers)</span>
                        </div>
                        <div class="text-caption grey--text">
                          Stock: {{ formatStock(item.seller_product_variants_sum_stock || 0) }}
                        </div>
                      </div>
                    </template>
                    <div v-if="item.seller_product_variants && item.seller_product_variants.length">
                      <div class="text-caption font-weight-bold mb-1">Seller Breakdown</div>
                      <div v-for="spv in item.seller_product_variants" :key="spv.id" class="text-caption mb-1">
                        <span class="font-weight-medium">{{ spv.seller ? spv.seller.first_name + ' ' + spv.seller.last_name : 'Unknown' }}</span>:
                        ₹{{ formatPrice(spv.maximum_retail_price || 0) }} · Stock: {{ spv.stock || 0 }}
                      </div>
                    </div>
                  </v-tooltip>
                </template>

                <!-- Dimensions Column -->
                <template #[`item.dimensions`]="{ item }">
                  <div>
                    <div class="text-caption">
                      L×W×H: {{ formatDimensions(item) }}
                    </div>
                    <div class="text-caption grey--text">
                      Weight: {{ item.weight ? item.weight + "g" : "N/A" }}
                    </div>
                    <div class="text-caption grey--text">
                      Potency: {{ item.potency || "N/A" }}
                    </div>
                  </div>
                </template>

                <!-- Stock Status Column -->
                <template #[`item.stock_status`]="{ item }">
                  <v-chip :color="getStockStatusColor(item.seller_product_variants_sum_stock)" x-small dark>
                    <v-icon left x-small>
                      {{ getStockStatusIcon(item.seller_product_variants_sum_stock) }}
                    </v-icon>
                    {{ getStockStatus(item.seller_product_variants_sum_stock) }}
                  </v-chip>
                </template>

                <!-- Total Value Column -->
                <template #[`item.total_value`]="{ item }">
                  <v-chip color="primary" x-small dark>
                    <v-icon left x-small>mdi-calculator</v-icon>
                    ₹{{
                      formatPrice(
                        (item.seller_product_variants_min_maximum_retail_price || 0) * (item.seller_product_variants_sum_stock || 0)
                      )
                    }}
                  </v-chip>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- Edit Button (View for Sellers, Edit for Admins) -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/products/${
                              $route.params.productId
                            }/product-variants/${encryptIt(item.id)}`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>{{
                              positionName === "SELLER"
                                ? "mdi-eye"
                                : "mdi-pencil"
                            }}</v-icon>
                          </v-btn>
                        </template>
                        <span>{{
                          positionName === "SELLER"
                            ? "View Product Variant"
                            : "Edit Product Variant"
                        }}</span>
                      </v-tooltip>

                      <!-- Product Question & Answer Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="info"
                            :to="`/products/${
                              $route.params.productId
                            }/product-variants/${encryptIt(item.id)}/questions`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-comment-question-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Product Question &amp; Answer</span>
                      </v-tooltip>

                      <!-- Product Reviews Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="amber darken-2"
                            dark
                            :to="`/products/${
                              $route.params.productId
                            }/product-variants/${encryptIt(item.id)}/reviews`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-star-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Product Reviews</span>
                      </v-tooltip>

                      <!-- Delete Button (Admin Only) -->
                      <v-tooltip bottom v-if="positionName != 'SELLER'">
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="showDeleteConfirmation(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Product Variant</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="Restore_this(item.id)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Product Variant</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-shopping-search</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      No Product Variants Found
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No product variants match your current filters. Try adjusting your search criteria."
                          : "You haven't added any product variants yet."
                      }}
                    </p>
                    <v-btn
                      v-if="
                        !hasActiveFilters &&
                        !isDeletePage() &&
                        positionName != 'SELLER'
                      "
                      color="primary"
                      :to="`/products/${$route.params.productId}/product-variants/create`"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Product Variant
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Success Dialog -->
      <v-dialog v-model="is_deleted_product_variant" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Product Variant deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_product_variant = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Restore Success Dialog -->
      <v-dialog v-model="is_restored_product_variant" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Product Variant restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_product_variant = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this product variant?
              </h3>

              <div
                class="product-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3">
                    <v-img
                      v-if="
                        productVariantToDelete &&
                        getFirstProductVariantImage(productVariantToDelete)
                      "
                      :src="
                        getImageUrl(
                          getFirstProductVariantImage(productVariantToDelete)
                        )
                      "
                      :alt="productVariantToDelete?.product?.name"
                    ></v-img>
                    <v-icon v-else color="grey lighten-1" size="24"
                      >mdi-shopping</v-icon
                    >
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{
                        productVariantToDelete?.product?.name ||
                        "No product name"
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      SKU:
                      {{ productVariantToDelete?.sku_id || "No SKU" }}
                    </div>
                    <div class="text-caption grey--text">
                      Price: ${{
                        productVariantToDelete
                          ? formatPrice(
                              productVariantToDelete.maximum_retail_price || 0
                            )
                          : "0.00"
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will move the product
                variant to the trash.
              </p>
              <p class="text-body-2 grey--text">
                You can restore it later from the deleted product variants
                section.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="cancelDelete"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="confirmDelete"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Product Variant
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ProductVariantsPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        stock_level: "",
        min_price: "",
        max_price: "",
        show_deleted: false,
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      jumpToPage: 1,
      is_deleted_product_variant: false,
      is_restored_product_variant: false,
      showDeleteDialog: false,
      productVariantToDelete: null,
      isDeleting: false,
      product_variants: [],
      product: null,
      statuses: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      stockLevels: [
        { text: "In Stock", value: "in_stock" },
        { text: "Low Stock", value: "low_stock" },
        { text: "Out of Stock", value: "out_of_stock" },
      ],
      headers: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "50px",
          align: "center",
        },
        {
          text: "Unique ID",
          value: "id",
          sortable: true,
          width: "80px",
          align: "center",
        },
        {
          text: "SKU ID",
          value: "sku_id",
          sortable: true,
          width: "100px",
          align: "center",
        },
        {
          text: "Created By",
          value: "created_by_id",
          sortable: true,
          width: "110px",
        },
        {
          text: "Product Variant Info",
          value: "product_variant_info",
          sortable: false,
          width: "160px",
        },
        {
          text: "Unit & Packing",
          value: "unit_packing",
          sortable: false,
          width: "120px",
        },
        {
          text: "Pricing",
          value: "pricing_info",
          sortable: false,
          width: "120px",
        },
        {
          text: "Price & Stock",
          value: "price_stock",
          sortable: false,
          width: "100px",
        },
        {
          text: "Dimensions",
          value: "dimensions",
          sortable: false,
          width: "110px",
        },
        {
          text: "Stock Status",
          value: "stock_status",
          sortable: false,
          width: "90px",
        },
        {
          text: "Total Value",
          value: "total_value",
          sortable: false,
          width: "80px",
        },
        { text: "Status", value: "is_active", sortable: false, width: "80px" },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "200px",
          align: "center",
        },
      ],
      isLoading: false,
      isExcelloading: false,
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.is_active ||
        this.filter.stock_level ||
        this.filter.min_price ||
        this.filter.max_price
      );
    },
    positionName() {
      return this.$store.getters["auth/user"]?.position?.name || "";
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },

  mounted() {
    this.jumpToPage = this.page;
    this.getProduct();
    this.getData();
  },

  methods: {
    async getData() {
      console.log("Get Product Variants Data");
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;

      // Ensure all filter values are properly set
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.is_active = this.filter.is_active || "";
      this.filter.stock_level = this.filter.stock_level || "";
      this.filter.min_price = this.filter.min_price || "";
      this.filter.max_price = this.filter.max_price || "";

      // Build query params using URLSearchParams
      const params = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.itemsPerPage,
        ...this.filter,
      });

      try {
        const productId = this.decryptIt(this.$route.params.productId);
        params.append("product_id", productId);
        const response = await axios.get(`/product_variants?${params}`);
        this.product_variants = response.data.data;
        this.count = response.data.count;
        this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Failed to load product variants",
          });
        }
      }
      this.isLoading = false;
    },

    showDeleteConfirmation(product) {
      this.productVariantToDelete = product;
      this.showDeleteDialog = true;
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.productVariantToDelete = null;
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.productVariantToDelete) return;

      this.isDeleting = true;

      try {
        await axios.post(
          `/product_variants/delete/${this.encryptIt(
            this.productVariantToDelete.id
          )}`
        );
        this.showDeleteDialog = false;
        this.productVariantToDelete = null;
        this.isDeleting = false;
        this.is_deleted_product_variant = true;
        this.getData();
      } catch (error) {
        console.log(error);
        this.isDeleting = false;
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Failed to delete product variant",
          });
        }
      }
    },

    async Delete_this(id) {
      try {
        await axios.post(`/product_variants/delete/${this.encryptIt(id)}`);
        this.is_deleted_product_variant = true;
        this.getData();
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Failed to delete product variant",
          });
        }
      }
    },

    async Restore_this(id) {
      try {
        await axios.post(`/product_variants/restore/${this.encryptIt(id)}`);
        this.is_restored_product_variant = true;
        this.getData();
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Failed to restore product variant",
          });
        }
      }
    },

    async generateExcel() {
      this.isExcelloading = true;

      let query =
        "show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&stock_level=" +
        this.filter.stock_level +
        "&min_price=" +
        this.filter.min_price +
        "&max_price=" +
        this.filter.max_price +
        "&search_keyword=" +
        this.filter.search_keyword;

      try {
        const productId = this.decryptIt(this.$route.params.productId);
        query += `&product_id=${productId}`;
        const response = await axios.get(`/product_variants/excel?${query}`, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `product_variants_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Failed to export product variants",
          });
        }
      }

      this.isExcelloading = false;
    },

    async getProduct() {
      try {
        const productId = this.$route.params.productId;
        const response = await axios.get(`/products/${productId}`);
        if (response.data.success) {
          this.product = response.data.data;
        }
      } catch (error) {
        console.log("Error loading product:", error);
        if (error.response && error.response.status === 404) {
          // Product not found, redirect back
          this.$router.push("/products");
        }
      }
    },

    isDeletePage() {
      return this.$route.query.open_trash ? true : false;
    },

    formatPrice(price) {
      return parseFloat(price || 0).toFixed(2);
    },

    formatStock(stock) {
      return parseFloat(stock || 0).toFixed(0);
    },

    formatDimensions(item) {
      const length = item.length || "N/A";
      const width = item.width || "N/A";
      const height = item.height || "N/A";

      if (length === "N/A" && width === "N/A" && height === "N/A") {
        return "N/A";
      }

      return `${length}×${width}×${height}`;
    },

    getStockStatus(stock) {
      const s = stock == null ? 0 : Number(stock);
      if (s <= 0) return "Out of Stock";
      if (s <= 10) return "Low Stock";
      return "In Stock";
    },

    getStockStatusColor(stock) {
      const s = stock == null ? 0 : Number(stock);
      if (s <= 0) return "error";
      if (s <= 10) return "warning";
      return "success";
    },

    getStockStatusIcon(stock) {
      const s = stock == null ? 0 : Number(stock);
      if (s <= 0) return "mdi-close-circle";
      if (s <= 10) return "mdi-alert-circle";
      return "mdi-check-circle";
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },

    clearAllFilters() {
      this.filter.search_keyword = "";
      this.filter.is_active = "";
      this.filter.stock_level = "";
      this.filter.min_price = "";
      this.filter.max_price = "";
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active) count++;
      if (this.filter.stock_level) count++;
      if (this.filter.min_price) count++;
      if (this.filter.max_price) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        // Reset to current page if invalid input
        this.jumpToPage = this.page;
      }
    },

    // Image helper methods
    getFirstProductVariantImage(product) {
      if (product && product.images && product.images.length > 0) {
        return product.images[0].image_url;
      }
      return null;
    },

    getImageUrl(imagePath) {
      if (!imagePath) return "";

      // If it's already a full URL, return it
      if (imagePath.startsWith("http")) {
        return imagePath;
      }

      // Use Vultr object storage URL (matching create page)
      const baseUrl = "https://blr1.vultrobjects.com/space-1/";
      return baseUrl + imagePath.replace(/^\//, "");
    },
  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Delete Confirmation Dialog Styling */
.product-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.product-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>